export const SUPPORTED_LANGUAGES = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["value"];

export function getLanguageLabel(code: string): string {
  return SUPPORTED_LANGUAGES.find((l) => l.value === code)?.label ?? code;
}

/** Quote PDF labels per language */
export const QUOTE_LABELS: Record<
  string,
  {
    title: string;
    quoteNumber: string;
    quoteDate: string;
    validUntil: string;
    offerTitle: string;
    customerAddress: string;
    yourReference: string;
    yourContact: string;
    ourContact: string;
    eventDate: string;
    planningDate: string;
    amount: string;
    factor: string;
    name: string;
    unitPrice: string;
    discount: string;
    totalCol: string;
    netPrice: string;
    total: string;
    overview: string;
    options: string;
    detail: string;
    sum: string;
    projectDiscount: string;
    priceExclVat: string;
    vat: string;
    vatReverseCharge: string;
    priceInclVat: string;
    optional: string;
    currency: string;
    currencyPrefix: string;
    generalConditions: string;
    signatureField: string;
    signatureDatePlace: string;
  }
> = {
  de: {
    title: "Angebot",
    quoteNumber: "Angebotsnummer:",
    quoteDate: "Angebotsdatum:",
    validUntil: "Gültig bis:",
    offerTitle: "Offerte",
    customerAddress: "Kundenadresse:",
    yourReference: "Kundennummer:",
    yourContact: "Unser Kontakt:",
    ourContact: "Ihr Kontakt:",
    eventDate: "Eventdatum:",
    planningDate: "Planungsdatum:",
    amount: "An.",
    factor: "Fakt.",
    name: "Name",
    unitPrice: "Preis p. E.",
    discount: "Rab.",
    totalCol: "Total CHF",
    netPrice: "Netto CHF",
    total: "Gesamt",
    overview: "Übersicht",
    options: "Optionen",
    detail: "Detail",
    sum: "Summe (exkl. Optionen):",
    projectDiscount: "Projektrabatt:",
    priceExclVat: "Preis exkl. MwSt.:",
    vat: "MwSt.",
    vatReverseCharge:
      "MwSt. – Übergang der Steuerschuld auf den Leistungsempfänger:",
    priceInclVat: "Preis inkl. MwSt.:",
    optional: "(Optional)",
    currency: "CHF",
    currencyPrefix: "Fr.",
    generalConditions: "Konditionen",
    signatureField: "Unterschrift",
    signatureDatePlace: "Datum und Ort",
  },
  en: {
    title: "Quotation",
    quoteNumber: "Quote number:",
    quoteDate: "Quote date:",
    validUntil: "Valid until:",
    offerTitle: "Quote",
    customerAddress: "Customer address:",
    yourReference: "Customer number:",
    yourContact: "Our contact:",
    ourContact: "Your contact:",
    eventDate: "Event date:",
    planningDate: "Planning date:",
    amount: "Qty",
    factor: "Fact.",
    name: "Name",
    unitPrice: "Unit price",
    discount: "Disc.",
    totalCol: "Total CHF",
    netPrice: "Net CHF",
    total: "Total",
    overview: "Overview",
    options: "Options",
    detail: "Detail",
    sum: "Sum (excl. options):",
    projectDiscount: "Project discount:",
    priceExclVat: "Price excl. VAT:",
    vat: "VAT",
    vatReverseCharge: "VAT – Reverse charge applies:",
    priceInclVat: "Price incl. VAT:",
    optional: "(Optional)",
    currency: "CHF",
    currencyPrefix: "CHF",
    generalConditions: "Conditions",
    signatureField: "Signature",
    signatureDatePlace: "Date and Place",
  },
  fr: {
    title: "Offre",
    quoteNumber: "Numéro d'offre:",
    quoteDate: "Date de l'offre:",
    validUntil: "Valable jusqu'au:",
    offerTitle: "Offre",
    customerAddress: "Adresse du client:",
    yourReference: "Numéro client:",
    yourContact: "Notre contact:",
    ourContact: "Votre contact:",
    eventDate: "Date de l'événement:",
    planningDate: "Date de planification:",
    amount: "Qté",
    factor: "Fact.",
    name: "Nom",
    unitPrice: "Prix unitaire",
    discount: "Rem.",
    totalCol: "Total CHF",
    netPrice: "Net CHF",
    total: "Total",
    overview: "Aperçu",
    options: "Options",
    detail: "Détail",
    sum: "Somme (hors options):",
    projectDiscount: "Remise projet:",
    priceExclVat: "Prix hors TVA:",
    vat: "TVA",
    vatReverseCharge: "TVA – Autoliquidation:",
    priceInclVat: "Prix TTC:",
    optional: "(Optionnel)",
    currency: "CHF",
    currencyPrefix: "CHF",
    generalConditions: "Conditions",
    signatureField: "Signature",
    signatureDatePlace: "Date et lieu",
  },
};

export function getQuoteLabels(language: string) {
  return QUOTE_LABELS[language] ?? QUOTE_LABELS.de;
}

/** Invoice PDF labels per language */
export const INVOICE_LABELS: Record<
  string,
  {
    title: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    paymentTerms: string;
    customerAddress: string;
    yourReference: string;
    yourContact: string;
    ourContact: string;
    eventDate: string;
    planningDate: string;
    amount: string;
    factor: string;
    name: string;
    unitPrice: string;
    discount: string;
    totalCol: string;
    netPrice: string;
    total: string;
    overview: string;
    options: string;
    detail: string;
    sum: string;
    projectDiscount: string;
    priceExclVat: string;
    vat: string;
    vatReverseCharge: string;
    priceInclVat: string;
    optional: string;
    currency: string;
    currencyPrefix: string;
    invoicePercentage: string;
    paymentInfo: string;
    qrBillPayableTo: string;
    qrBillPayableBy: string;
    qrBillCurrency: string;
    qrBillAmount: string;
    qrBillReference: string;
  }
> = {
  de: {
    title: "Rechnung",
    invoiceNumber: "Rechnungsnummer:",
    invoiceDate: "Rechnungsdatum:",
    dueDate: "Fälligkeitsdatum:",
    paymentTerms: "Zahlungsfrist:",
    customerAddress: "Kundenadresse:",
    yourReference: "Kundennummer:",
    yourContact: "Unser Kontakt:",
    ourContact: "Ihr Kontakt:",
    eventDate: "Eventdatum:",
    planningDate: "Planungsdatum:",
    amount: "An.",
    factor: "Fakt.",
    name: "Name",
    unitPrice: "Preis p. E.",
    discount: "Rab.",
    totalCol: "Total CHF",
    netPrice: "Netto CHF",
    total: "Gesamt",
    overview: "Übersicht",
    options: "Optionen",
    detail: "Detail",
    sum: "Summe (exkl. Optionen):",
    projectDiscount: "Projektrabatt:",
    priceExclVat: "Preis exkl. MwSt.:",
    vat: "MwSt.",
    vatReverseCharge:
      "MwSt. – Übergang der Steuerschuld auf den Leistungsempfänger:",
    priceInclVat: "Preis inkl. MwSt.:",
    optional: "(Optional)",
    currency: "CHF",
    currencyPrefix: "Fr.",
    invoicePercentage: "Rechnungsbetrag",
    paymentInfo: "Zahlungsinformationen",
    qrBillPayableTo: "Konto / Zahlbar an",
    qrBillPayableBy: "Zahlbar durch",
    qrBillCurrency: "Währung",
    qrBillAmount: "Betrag",
    qrBillReference: "Referenz",
  },
  en: {
    title: "Invoice",
    invoiceNumber: "Invoice number:",
    invoiceDate: "Invoice date:",
    dueDate: "Due date:",
    paymentTerms: "Payment terms:",
    customerAddress: "Customer address:",
    yourReference: "Customer number:",
    yourContact: "Our contact:",
    ourContact: "Your contact:",
    eventDate: "Event date:",
    planningDate: "Planning date:",
    amount: "Qty",
    factor: "Fact.",
    name: "Name",
    unitPrice: "Unit price",
    discount: "Disc.",
    totalCol: "Total CHF",
    netPrice: "Net CHF",
    total: "Total",
    overview: "Overview",
    options: "Options",
    detail: "Detail",
    sum: "Sum (excl. options):",
    projectDiscount: "Project discount:",
    priceExclVat: "Price excl. VAT:",
    vat: "VAT",
    vatReverseCharge: "VAT – Reverse charge applies:",
    priceInclVat: "Price incl. VAT:",
    optional: "(Optional)",
    currency: "CHF",
    currencyPrefix: "CHF",
    invoicePercentage: "Invoice amount",
    paymentInfo: "Payment information",
    qrBillPayableTo: "Account / Payable to",
    qrBillPayableBy: "Payable by",
    qrBillCurrency: "Currency",
    qrBillAmount: "Amount",
    qrBillReference: "Reference",
  },
  fr: {
    title: "Facture",
    invoiceNumber: "Numéro de facture:",
    invoiceDate: "Date de facturation:",
    dueDate: "Date d'échéance:",
    paymentTerms: "Conditions de paiement:",
    customerAddress: "Adresse du client:",
    yourReference: "Numéro client:",
    yourContact: "Notre contact:",
    ourContact: "Votre contact:",
    eventDate: "Date de l'événement:",
    planningDate: "Date de planification:",
    amount: "Qté",
    factor: "Fact.",
    name: "Nom",
    unitPrice: "Prix unitaire",
    discount: "Rem.",
    totalCol: "Total CHF",
    netPrice: "Net CHF",
    total: "Total",
    overview: "Aperçu",
    options: "Options",
    detail: "Détail",
    sum: "Somme (hors options):",
    projectDiscount: "Remise projet:",
    priceExclVat: "Prix hors TVA:",
    vat: "TVA",
    vatReverseCharge: "TVA – Autoliquidation:",
    priceInclVat: "Prix TTC:",
    optional: "(Optionnel)",
    currency: "CHF",
    currencyPrefix: "CHF",
    invoicePercentage: "Montant facturé",
    paymentInfo: "Informations de paiement",
    qrBillPayableTo: "Compte / Payable à",
    qrBillPayableBy: "Payable par",
    qrBillCurrency: "Monnaie",
    qrBillAmount: "Montant",
    qrBillReference: "Référence",
  },
};

export function getInvoiceLabels(language: string) {
  return INVOICE_LABELS[language] ?? INVOICE_LABELS.de;
}
